import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingTabComponent } from '../../../components/ui/tabs/routing-tab/routing-tab.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { MessageTemplateTable } from '../../../components/message-template-table/message-table.component';
import { MessageService } from '../../../services/message/message.service';
import { MessageTemplate } from '../../../services/message-templates/message.template';
import { ActiveMessagesTableComponent } from './select-message-recipient/partials/active-messages-table/active-messages-table.component';
import { InactiveMessagesTableComponent } from './select-message-recipient/partials/inactive-messages-table/inactive-messages-table.component';
import { ArchivedMessagesComponent } from './select-message-recipient/partials/archived-messages/archived-messages.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { map } from 'rxjs/operators';
import { LiveRefresh } from '../../../services/live/live-refresh';

/** Tab counts follow the lists: new sends, archives and drafts show up within this. */
const COUNTS_LIVE_MS = 30_000;

export type MessageTab = 'active' | 'inactive' | 'templates' | 'archived';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [
    RoutingTabComponent,
    CommonModule,
    MessageTemplateTable,
    ActiveMessagesTableComponent,
    InactiveMessagesTableComponent,
    ArchivedMessagesComponent,
    HasPermissionDirective,
    EmptyStateComponent,
    NgClickOutsideDirective,
  ],
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.css'],
})
export class MessagesComponent implements OnInit {
  readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly messageTemplate = inject(MessageTemplate);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  @ViewChild('createTrigger') createTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('createMenu') createMenu?: ElementRef<HTMLElement>;

  activeTab: MessageTab = 'active';

  showCreateDropDown = false;

  template_data: any[] = [];
  loading: boolean = false;
  meta_template: any = {};

  startItem_template: number = 0;
  endItem_template: number = 0;
  total_templates: number = 0;

  /** One line under the heading saying what the current tab holds. */
  get description(): string {
    switch (this.activeTab) {
      case 'inactive':
        return 'Drafts and cancelled sends.';
      case 'templates':
        return 'Reusable copy for your messages.';
      case 'archived':
        return 'Sent messages filed away from the active list.';
      default:
        return 'Broadcasts that are sending, scheduled or sent. The list keeps itself up to date.';
    }
  }

  tabs = [
    {
      title: 'Active',
      count: 0,
      link: '/organization/messages',
      queryParams: {} as Record<string, string>,
    },
    {
      title: 'Inactive',
      count: 0,
      link: '/organization/messages',
      queryParams: { tab: 'inactive' },
    },
    {
      title: 'Templates',
      count: 0,
      link: '/organization/messages',
      queryParams: { tab: 'templates' },
    },
    {
      title: 'Archived',
      count: 0,
      link: '/organization/messages',
      queryParams: { tab: 'archived' },
    },
  ];

  private updateTabCounts(): void {
    this.tabs = this.tabs.map((tab, i) =>
      i === 2 ? { ...tab, count: this.total_templates } : tab,
    );
  }

  toggleCreateDropDown(event?: Event): void {
    this.showCreateDropDown = !this.showCreateDropDown;
    // Enter or Space clicks with detail 0: carry keyboard focus into the menu.
    if (this.showCreateDropDown && (event as MouseEvent | undefined)?.detail === 0) {
      this.focusCreateItem(0);
    }
  }

  closeCreateDropDown(): void {
    this.showCreateDropDown = false;
  }

  /** Arrow keys on the "New message" button open the menu at its first or last item. */
  onCreateTriggerKeydown(event: Event): void {
    const { key } = event as KeyboardEvent;
    if (key !== 'ArrowDown' && key !== 'ArrowUp') return;
    event.preventDefault();
    this.showCreateDropDown = true;
    this.focusCreateItem(key === 'ArrowUp' ? -1 : 0);
  }

  /** Arrow, Home and End move through the menu; Tab closes it and carries on from the button. */
  onCreateMenuKeydown(event: Event): void {
    const { key } = event as KeyboardEvent;
    const items = this.createItems();
    if (!items.length) return;

    if (key === 'Tab') {
      this.createTrigger?.nativeElement.focus();
      this.closeCreateDropDown();
      return;
    }

    const current = items.indexOf(event.target as HTMLElement);
    let next: number;
    switch (key) {
      case 'ArrowDown':
        next = current >= items.length - 1 ? 0 : current + 1;
        break;
      case 'ArrowUp':
        next = current <= 0 ? items.length - 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    items[next].focus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.showCreateDropDown) return;
    const focused = this.document.activeElement;
    const focusWasInMenu = !!focused && !!this.createMenu?.nativeElement.contains(focused);
    this.closeCreateDropDown();
    if (focusWasInMenu) this.createTrigger?.nativeElement.focus();
  }

  private createItems(): HTMLElement[] {
    const menu = this.createMenu?.nativeElement;
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
  }

  /** Focuses a menu item once the menu has rendered; a negative index counts from the end. */
  private focusCreateItem(index: number): void {
    setTimeout(() => {
      const items = this.createItems();
      items[index < 0 ? items.length + index : index]?.focus();
    });
  }

  goToComposeMessage(): void {
    this.closeCreateDropDown();
    this.router.navigate(['/organization/messages/create']);
  }

  goToSmartBroadcast(): void {
    this.closeCreateDropDown();
    this.router.navigate(['/organization/smart-broadcast']);
  }

  toggleContactDropDownTemplate(): void {
    this.router.navigate(['/organization/messages/templates/new']);
  }

  onClickedOutside(_e: Event): void {
    this.closeCreateDropDown();
  }

  openCreateOrganizationModal(): void {}
  openCreateTagModal(): void {}

  goToCreateSegmentPage(): void {
    this.router.navigate(['/organization/contacts/segmented-list/create']);
  }

  ngOnInit(): void {
    // Track active tab from query params — single component instance survives all tab switches
    this.activatedRoute.queryParamMap
      .pipe(
        map((params) => (params.get('tab') as MessageTab) ?? 'active'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((tab) => {
        this.activeTab = tab;
        if (tab === 'templates') {
          this.messageTemplate.fetchTemplates();
        }
      });

    // Subscribe to cached counts (prefetched by PortalComponent on load)
    // Spread into a new array so OnPush RoutingTabComponent detects the change
    this.messageService.getActiveMessagesCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.tabs = this.tabs.map((tab, i) =>
          i === 0 ? { ...tab, count } : tab,
        );
      });

    this.messageService.getInactiveMessagesCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.tabs = this.tabs.map((tab, i) =>
          i === 1 ? { ...tab, count } : tab,
        );
      });

    this.messageService.getArchiveMessagesCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => {
        this.tabs = this.tabs.map((tab, i) =>
          i === 3 ? { ...tab, count } : tab,
        );
      });

    // Refresh counts on this page visit, then keep them current
    this.messageService.prefetchAllCounts();
    const liveCounts = new LiveRefresh(this.zone, {
      refresh: () => this.messageService.prefetchAllCounts(),
      interval: () => COUNTS_LIVE_MS,
    });
    liveCounts.start();
    this.destroyRef.onDestroy(() => liveCounts.stop());

    // Wire template data + pagination subscriptions once
    this.messageTemplate.getTemplates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.template_data = data;
      });

    this.messageTemplate.loading
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading: boolean) => {
        this.loading = loading;
      });

    this.messageTemplate.pagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((paginate: any) => {
        if (!paginate) return;
        const currentPage = paginate.currentPage + 1;
        const itemsPerPage = paginate.pageSize;
        const totalItems = paginate.totalElements;

        this.startItem_template = (currentPage - 1) * itemsPerPage + 1;
        this.endItem_template = Math.min(
          currentPage * itemsPerPage,
          totalItems,
        );
        this.total_templates = totalItems;
        this.meta_template = paginate;
        this.updateTabCounts();
      });

    // Trigger initial template fetch
    this.messageTemplate.fetchTemplates();
  }
}
